// src/pages/APropos.jsx
import React from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";

const Apropos = () => {
  const navigate = useNavigate();

  const missions = [
    {
      title: "Innovation",
      description: "Utilisation des dernières technologies pour améliorer l'accès aux soins",
      icon: "💡"
    },
    {
      title: "Accessibilité",
      description: "Des services de santé disponibles pour tous, partout en Afrique",
      icon: "🌍"
    },
    {
      title: "Qualité",
      description: "Des professionnels de santé qualifiés et des protocoles rigoureux",
      icon: "✨"
    },
    {
      title: "Prévention",
      description: "Sensibilisation et éducation pour une meilleure santé publique",
      icon: "🎯"
    }
  ];

  const stats = [
    { number: "50k+", label: "Consultations" },
    { number: "100+", label: "Médecins" },
    { number: "30+", label: "Pays" },
    { number: "95%", label: "Satisfaction" }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />

      {/* Hero Section */}
      <motion.section
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="bg-blue-700 text-white py-20"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.h1
            initial={{ y: -20 }}
            animate={{ y: 0 }}
            className="text-4xl md:text-5xl font-bold mb-6"
          >
            Notre Vision pour l'Afrique
          </motion.h1>
          <motion.p
            initial={{ y: 20 }}
            animate={{ y: 0 }}
            className="text-xl md:text-2xl text-blue-100 max-w-3xl mx-auto"
          >
            Transformer l'accès aux soins de santé grâce à l'innovation numérique
          </motion.p>
        </div>
      </motion.section>

      {/* Mission Section */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900">Notre Mission</h2>
            <p className="mt-4 text-xl text-gray-600">
              Rendre les soins de santé accessibles à tous en Afrique
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {missions.map((mission, index) => (
              <motion.div
                key={mission.title}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="bg-white p-6 rounded-lg shadow-lg"
              >
                <div className="text-4xl mb-4">{mission.icon}</div>
                <h3 className="text-xl font-semibold mb-2">{mission.title}</h3>
                <p className="text-gray-600">{mission.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="bg-blue-50 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <motion.div
                key={stat.label}
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: index * 0.1 }}
                className="text-center"
              >
                <div className="text-4xl font-bold text-blue-700">{stat.number}</div>
                <div className="text-gray-600 mt-2">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl font-bold text-gray-900 mb-8">
              Nos Valeurs
            </h2>
            <div className="prose prose-lg text-gray-600">
              <p className="mb-4">
                La Clinique Numérique Africaine est née de la conviction que la technologie
                peut transformer l'accès aux soins de santé en Afrique. Notre approche
                combine l'expertise médicale avec l'innovation numérique pour offrir
                des solutions adaptées aux besoins locaux.
              </p>
              <p>
                Nous nous engageons à maintenir les plus hauts standards de qualité
                et d'éthique dans tous nos services, tout en restant accessibles
                au plus grand nombre.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-gray-900 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-8">
            Rejoignez-nous dans cette aventure
          </h2>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => navigate("/contact")}
            className="bg-white text-gray-900 px-8 py-3 rounded-full font-semibold hover:bg-gray-100 transition-colors"
          >
            Contactez-nous
          </motion.button>
        </div>
      </section>
    </div>
  );
};

export default Apropos;